// Embedded business login — drives Authentik's headless flow-executor +
// authorization-code/PKCE from our own login screen (no hosted Authentik page).
//
// Adapted from ihjizli-admin/src/lib/authentik_flow.ts (same redirect/consent
// handling, which was hard-won against a live Authentik) for the BUSINESS
// application (infra authentik/blueprints/02-business.yaml):
//   client `business-web` (public, PKCE) · brand host auth.business.… (the
//   same-origin proxy sends it as Host) · flow `business-authentication`:
//     identification (uid_field = 'biz:+964…', ADR-0018) → lockout deny stage
//     (LOCKED:<seconds>) → password stage (carries the 6-digit PIN) → login
//   No MFA stage exists on this flow.
// ENROLLMENT (web onboarding): enroll() / resendOtp() / submitOtp() / setPin()
// drive `business-enrollment` the same way the business app does (see the
// section below). login() is unchanged.
//
// SAME-ORIGIN: every call hits Authentik at its REAL paths (/api/v3,
// /application, /if) through the proxy, because the executor redirects to
// absolute paths that the browser resolves against this origin.

import { AuthError, AUTH_COPY, parseLocked } from './types';

const AUTHK = '';
const SCOPES = 'openid profile email offline_access';
const DEFAULT_FLOW_SLUG = 'business-authentication';

const C_IDENT = 'ak-stage-identification';
const C_PASSWORD = 'ak-stage-password';
const C_CONSENT = 'ak-stage-consent';
const C_REDIRECT = 'xak-flow-redirect';
const C_DENIED = 'ak-stage-access-denied';
const C_SMS = 'ak-stage-authenticator-sms';
const C_PROMPT = 'ak-stage-prompt';

export interface FlowClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  phone_number?: string;
  email?: string;
}

export interface FlowTokens {
  accessToken: string;
  idToken: string;
  claims: FlowClaims;
  refreshToken: string;
  expiresIn: number;
}

export interface ConsentPermission {
  id?: string;
  name?: string;
}

export interface ConsentChallenge {
  token?: string;
  permissions?: ConsentPermission[];
  additional_permissions?: ConsentPermission[];
}

interface Challenge {
  component?: string;
  to?: string;
  error_message?: string;
  response_errors?: Record<string, Array<{ string?: string; code?: string }>>;
  token?: string;
  permissions?: ConsentPermission[];
  additional_permissions?: ConsentPermission[];
  [k: string]: unknown;
}

export interface FlowClientOptions {
  /**
   * The transport. Default: the global fetch, looked up per call (so tests
   * that stub it keep working). The mock build injects a scripted fake
   * Authentik here (features/onboarding/mock/authentik.ts).
   */
  fetch?: typeof fetch;
}

export class AuthentikFlowClient {
  private execUrl: string | null = null;
  private codeVerifier = '';
  private authorizeQuery: URLSearchParams | null = null;
  private readonly redirectUri: string;
  private readonly http: typeof fetch;
  /** Enrollment in flight (enroll → submitOtp → setPin): the stage awaiting input. */
  private enrolling: { uid: string; phone: string; awaiting: typeof C_SMS | typeof C_PROMPT } | null = null;

  constructor(
    private readonly clientId: string,
    opts: FlowClientOptions = {},
  ) {
    this.redirectUri = `${window.location.origin}/auth/callback`;
    this.http = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  /** authorize (PKCE) → identification(biz:+964…) → PIN → tokens. Throws AuthError. */
  async login(uid: string, pin: string): Promise<FlowTokens> {
    try {
      if (await this.beginAuthorize()) {
        // A live Authentik session (lock-screen unlock, or the app's tokens
        // expired before it) makes authorize answer silently with a code, and
        // business-authentication is require_unauthenticated, so it would refuse
        // the PIN. The PIN must be checked regardless: end that session, then
        // authorize again. The silent code is never exchanged.
        await this.logout();
        if (await this.beginAuthorize()) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      }
      let c = await this.exec();
      c = await this.drive(c, uid, pin);
      if (c.component !== C_REDIRECT || !c.to) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      const code = await this.completeFromRedirect(c.to);
      if (!code) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      return await this.exchange(code);
    } catch (e) {
      if (e instanceof AuthError) throw e;
      if (e instanceof TypeError) throw new AuthError('network', AUTH_COPY.network);
      throw new AuthError('unexpected', AUTH_COPY.unexpected);
    } finally {
      this.execUrl = null;
      this.codeVerifier = '';
    }
  }

  /** End the first-party Authentik session (best effort). */
  async logout(): Promise<void> {
    const slug = 'default-invalidation-flow';
    await this.http(`${AUTHK}/if/flow/${slug}/`, { method: 'GET', credentials: 'include', redirect: 'follow' }).catch(() => {});
    this.execUrl = `${AUTHK}/api/v3/flows/executor/${slug}/?query=`;
    for (let i = 0; i < 4; i++) {
      let c: Challenge;
      try {
        c = await this.exec();
      } catch {
        return;
      }
      if (!isExecutorRedirect(c.to)) return;
    }
  }

  // --- enrollment (business-enrollment flow) --------------------------------
  //
  // Mirrors feature_auth AuthentikFlowClient.start/submitCode/submitNewPin for
  // the business audience (identifierPrefix 'biz:'), against the blueprint's
  // flows (infra authentik/blueprints/02-business.yaml):
  //   business-authentication: identification(10) — an UNKNOWN uid answers the
  //     identification challenge again with response_errors (pretend_user_exists
  //     false) and the challenge carries `enroll_url` (enrollment_flow =
  //     business-enrollment) → we switch the executor to that flow, keeping the
  //     authorize query so its end redirects back into /application/o/authorize/.
  //     A KNOWN uid moves on (lockout deny 15 → password 20) → already registered.
  //   business-enrollment: prompt(10) username = biz:+964…, attributes.phone =
  //     +964… → user-write(20) → authenticator-sms(30) phone_number → code →
  //     PIN prompt(40, field `password`, validation: shape + weak + device
  //     matches phone) → PIN write(50) → user login(60) → redirect.
  // State lives on this instance between the calls (one client per wizard).

  /** Start a new business account for `uid` (biz:+964…): identification → enrollment → code sent. Throws AuthError. */
  async enroll(uid: string, phone: string): Promise<'code_sent'> {
    this.resetEnrollment();
    try {
      await this.beginAuthorize();
      let c = await this.exec();
      for (let i = 0; i < 6 && c.component !== C_IDENT; i++) {
        if (c.component === C_DENIED) throw deniedError(c);
        if (c.component === C_REDIRECT && !isExecutorRedirect(c.to)) throw new AuthError('unexpected', AUTH_COPY.unexpected);
        c = await this.exec();
      }
      if (c.component !== C_IDENT) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      const enrollUrl = typeof c.enroll_url === 'string' && c.enroll_url ? c.enroll_url : null;
      const after = await this.exec('POST', { component: C_IDENT, uid_field: uid });
      if (after.component === C_DENIED) throw deniedError(after);
      if (!(after.component === C_IDENT && hasErrors(after))) {
        // The identification stage accepted the uid: this phone already has a business account.
        throw new AuthError('already_registered', AUTH_COPY.alreadyRegistered);
      }
      if (!enrollUrl) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      this.execUrl = executorUrlFromFlowLocation(enrollUrl, this.authorizeQuery ?? new URLSearchParams());
      this.enrolling = { uid, phone, awaiting: C_SMS };
      const stop = await this.advanceEnrollment(await this.exec());
      if (stop.component !== C_SMS) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      return 'code_sent';
    } catch (e) {
      this.resetEnrollment();
      throw asAuthError(e);
    }
  }

  /** Send the code again on the SAME flow (re-POST phone_number to the SMS stage). */
  async resendOtp(): Promise<void> {
    const st = this.enrolling;
    if (!st || st.awaiting !== C_SMS) throw new AuthError('flow_expired', AUTH_COPY.flowExpired);
    try {
      const r = await this.exec('POST', { component: C_SMS, phone_number: st.phone });
      if (r.component === C_DENIED) throw deniedError(r);
      if (hasErrors(r)) throw new AuthError('unexpected', firstError(r) ?? AUTH_COPY.unexpected);
    } catch (e) {
      throw asAuthError(e);
    }
  }

  /** Submit the verification code. Wrong code → AuthError('invalid_code'); the flow stays open. */
  async submitOtp(code: string): Promise<'pin_required'> {
    const st = this.enrolling;
    if (!st || st.awaiting !== C_SMS) throw new AuthError('flow_expired', AUTH_COPY.flowExpired);
    try {
      const r = await this.exec('POST', { component: C_SMS, code });
      // The SMS stage answering again = the code was not accepted (Authentik
      // raises «Code does not match» as a response error on the same stage).
      if (r.component === C_SMS) throw new AuthError('invalid_code', AUTH_COPY.invalidCode);
      const stop = await this.advanceEnrollment(r);
      if (stop.component !== C_PROMPT) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      st.awaiting = C_PROMPT;
      return 'pin_required';
    } catch (e) {
      const err = asAuthError(e);
      if (err.code !== 'invalid_code') this.resetEnrollment();
      throw err;
    }
  }

  /**
   * Set the first PIN, finish the flow and exchange the code for tokens.
   * A refused PIN (shape / weak / device check) → AuthError('weak_pin') with
   * the server's sentence; the flow stays open for another try.
   */
  async setPin(pin: string): Promise<FlowTokens> {
    const st = this.enrolling;
    if (!st || st.awaiting !== C_PROMPT) throw new AuthError('flow_expired', AUTH_COPY.flowExpired);
    try {
      const r = await this.exec('POST', { component: C_PROMPT, password: pin });
      if (r.component === C_PROMPT) throw new AuthError('weak_pin', firstError(r) ?? AUTH_COPY.pinWeak);
      const stop = await this.advanceEnrollment(r);
      if (stop.component !== C_REDIRECT || !stop.to) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      const authCode = await this.completeFromRedirect(stop.to);
      if (!authCode) throw new AuthError('unexpected', AUTH_COPY.unexpected);
      const tokens = await this.exchange(authCode);
      this.resetEnrollment();
      return tokens;
    } catch (e) {
      const err = asAuthError(e);
      if (err.code !== 'weak_pin') this.resetEnrollment();
      throw err;
    }
  }

  /** Whether an enrollment is waiting for a code ('otp') or a PIN ('pin'); null = none in flight. */
  enrollmentStage(): 'otp' | 'pin' | null {
    if (!this.enrolling) return null;
    return this.enrolling.awaiting === C_SMS ? 'otp' : 'pin';
  }

  resetEnrollment(): void {
    this.enrolling = null;
    this.execUrl = null;
    this.codeVerifier = '';
    this.authorizeQuery = null;
  }

  /**
   * Drive the enrollment executor through the stages that need nothing from
   * the person (identity prompt, SMS phone number, consent, executor hops),
   * stopping at one that does: the SMS code, the PIN prompt, or the terminal
   * redirect. Same field-driven prompt fill as the Flutter client.
   */
  private async advanceEnrollment(first: Challenge): Promise<Challenge> {
    const st = this.enrolling;
    if (!st) throw new AuthError('flow_expired', AUTH_COPY.flowExpired);
    let c = first;
    let phoneSent = false;
    for (let i = 0; i < 12; i++) {
      switch (c.component) {
        case C_DENIED:
          throw deniedError(c);
        case C_PROMPT: {
          const fields = fieldKeys(c);
          if (fields.includes('password')) return c;
          const body: Record<string, unknown> = { component: C_PROMPT };
          for (const key of fields) {
            if (key === 'username') body.username = st.uid;
            if (key === 'attributes.phone') body['attributes.phone'] = st.phone;
          }
          const next = await this.exec('POST', body);
          if (next.component === C_PROMPT && hasErrors(next)) throw new AuthError('unexpected', firstError(next) ?? AUTH_COPY.unexpected);
          c = next;
          break;
        }
        case C_SMS: {
          if (phoneSent) {
            if (hasErrors(c)) throw new AuthError('unexpected', firstError(c) ?? AUTH_COPY.unexpected);
            return c;
          }
          phoneSent = true;
          c = await this.exec('POST', { component: C_SMS, phone_number: st.phone });
          break;
        }
        case C_CONSENT:
          c = await this.exec('POST', consentPayload(c));
          break;
        case C_REDIRECT:
          if (isExecutorRedirect(c.to)) {
            c = await this.exec();
            break;
          }
          return c;
        default:
          if (hasErrors(c)) throw new AuthError('unexpected', firstError(c) ?? AUTH_COPY.unexpected);
          c = await this.exec();
      }
    }
    throw new AuthError('unexpected', AUTH_COPY.unexpected);
  }

  // --- flow driving -------------------------------------------------------

  private async drive(first: Challenge, uid: string, pin: string): Promise<Challenge> {
    let c = first;
    let identSent = false;
    let pinSent = false;
    for (let i = 0; i < 12; i++) {
      switch (c.component) {
        case C_IDENT:
          if (identSent && hasErrors(c)) throw new AuthError('invalid_credentials', AUTH_COPY.invalidPin);
          identSent = true;
          c = await this.exec('POST', { component: C_IDENT, uid_field: uid });
          break;
        case C_PASSWORD:
          // A re-presented password stage after we sent the PIN = wrong PIN.
          if (pinSent) throw new AuthError('invalid_credentials', AUTH_COPY.invalidPin);
          pinSent = true;
          c = await this.exec('POST', { component: C_PASSWORD, password: pin });
          break;
        case C_DENIED: {
          const secs = parseLocked(c.error_message);
          if (secs != null) throw new AuthError('locked', lockedMessage(secs), secs);
          throw new AuthError(pinSent ? 'invalid_credentials' : 'denied', pinSent ? AUTH_COPY.invalidPin : AUTH_COPY.denied);
        }
        case C_CONSENT:
          c = await this.exec('POST', consentPayload(c));
          break;
        case C_REDIRECT:
          if (isExecutorRedirect(c.to)) {
            c = await this.exec();
            break;
          }
          return c;
        default:
          if (hasErrors(c)) throw new AuthError('invalid_credentials', AUTH_COPY.invalidPin);
          c = await this.exec();
      }
    }
    throw new AuthError('unexpected', AUTH_COPY.unexpected);
  }

  /** Follow the terminal redirect (an authorize re-entry, possibly via a consent
   *  flow) until /auth/callback?code= — see the admin client for the rationale. */
  private async completeFromRedirect(to: string): Promise<string | null> {
    let target = to;
    for (let hop = 0; hop < 6; hop++) {
      const direct = callbackCode(target);
      if (direct) return direct;
      const res = await this.http(new URL(target, window.location.origin).toString(), {
        method: 'GET',
        credentials: 'include',
        redirect: 'follow',
        headers: { Accept: 'application/json' },
      });
      const landed = res.url || '';
      const code = callbackCode(landed);
      if (code) return code;
      if (landed.includes('/if/flow/')) {
        this.execUrl = executorUrlFromFlowLocation(landed, new URLSearchParams());
        let c = await this.exec();
        c = await this.drive(c, '', '');
        if (!c.to) return null;
        target = c.to;
        continue;
      }
      const body = await res.clone().text().catch(() => '');
      if (isAccessDenied(landed, body)) throw new AuthError('denied', AUTH_COPY.denied);
      try {
        const json = JSON.parse(body) as { to?: string; redirect?: string; location?: string };
        const next = json.to ?? json.redirect ?? json.location;
        if (next) {
          target = next;
          continue;
        }
      } catch {
        /* not JSON */
      }
      return null;
    }
    return null;
  }

  /** Start authorize (PKCE). True when a live session answered it silently with a code instead of a flow. */
  private async beginAuthorize(): Promise<boolean> {
    this.codeVerifier = randomString(40);
    const q = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: SCOPES,
      state: randomString(24),
      code_challenge: await pkceChallenge(this.codeVerifier),
      code_challenge_method: 'S256',
    });
    const res = await this.http(`${AUTHK}/application/o/authorize/?${q.toString()}`, {
      method: 'GET',
      credentials: 'include',
      redirect: 'follow',
      headers: { Accept: 'application/json' },
    });
    const loc = await resolveFlowLocation(res, q);
    this.authorizeQuery = q;
    this.execUrl = executorUrlFromFlowLocation(loc, q);
    return callbackCode(res.url || '') != null;
  }

  private async exec(method: 'GET' | 'POST' = 'GET', body?: Record<string, unknown>): Promise<Challenge> {
    if (!this.execUrl) throw new AuthError('unexpected', AUTH_COPY.unexpected);
    const res = await this.http(this.execUrl, {
      method,
      credentials: 'include',
      redirect: 'follow',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const finalUrl = res.url || '';
    if (finalUrl.includes('/auth/callback') && finalUrl.includes('code=')) return { component: C_REDIRECT, to: finalUrl };
    const text = await res.text();
    try {
      return JSON.parse(text) as Challenge;
    } catch {
      throw new AuthError('unexpected', AUTH_COPY.unexpected);
    }
  }

  private async exchange(code: string): Promise<FlowTokens> {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri,
      client_id: this.clientId,
      code_verifier: this.codeVerifier,
    });
    const res = await this.http(`${AUTHK}/application/o/token/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: body.toString(),
    });
    if (!res.ok) throw new AuthError('unexpected', AUTH_COPY.unexpected);
    return tokensFrom(await res.json(), '');
  }
}

/** OAuth2 refresh_token grant (stateless). Throws AuthError when rejected. */
export async function refreshTokens(clientId: string, refreshToken: string): Promise<FlowTokens> {
  const body = new URLSearchParams({ grant_type: 'refresh_token', refresh_token: refreshToken, client_id: clientId, scope: SCOPES });
  const res = await fetch(`${AUTHK}/application/o/token/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body: body.toString(),
  });
  if (!res.ok) throw new AuthError('unexpected', AUTH_COPY.unexpected);
  return tokensFrom(await res.json(), refreshToken);
}

function tokensFrom(
  data: { access_token?: string; id_token?: string; refresh_token?: string; expires_in?: number },
  previousRefresh: string,
): FlowTokens {
  if (!data.access_token) throw new AuthError('unexpected', AUTH_COPY.unexpected);
  return {
    accessToken: data.access_token,
    idToken: data.id_token ?? '',
    claims: decodeJwtClaims(data.id_token) ?? decodeJwtClaims(data.access_token) ?? {},
    refreshToken: data.refresh_token ?? previousRefresh,
    expiresIn: typeof data.expires_in === 'number' ? data.expires_in : 0,
  };
}

// --- pure helpers (exported for tests) -------------------------------------

/** 'الحساب مقفل مؤقتاً — حاول بعد ٠٥:٠٠' (business app copy, Arabic digits). */
export function lockedMessage(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const ar = `${mm}:${ss}`.replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);
  return `الحساب مقفل مؤقتاً — حاول بعد ${ar}`;
}

export function consentPayload(challenge: ConsentChallenge): Record<string, unknown> {
  const ids = [...(challenge.permissions ?? []), ...(challenge.additional_permissions ?? [])]
    .map((p) => p?.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  const body: Record<string, unknown> = { component: C_CONSENT, permissions: ids };
  if (typeof challenge.token === 'string' && challenge.token.length > 0) body.token = challenge.token;
  return body;
}

export function executorUrlFromFlowLocation(location: string, fallbackQuery: URLSearchParams): string {
  const slug = /\/if\/flow\/([^/?]+)\//.exec(location)?.[1];
  if (!slug) throw new AuthError('unexpected', AUTH_COPY.unexpected);
  let query: string;
  const qIdx = location.indexOf('?');
  if (qIdx >= 0) {
    const params = new URLSearchParams(location.slice(qIdx + 1));
    query = params.get('query') ?? location.slice(qIdx + 1);
  } else {
    query = fallbackQuery.toString();
  }
  return `${AUTHK}/api/v3/flows/executor/${slug}/?query=${encodeURIComponent(query)}`;
}

async function resolveFlowLocation(res: Response, q: URLSearchParams): Promise<string> {
  if (res.url && res.url.includes('/if/flow/')) return res.url;
  try {
    const data = (await res.clone().json()) as { redirect?: string; location?: string };
    const loc = data.redirect ?? data.location;
    if (loc && loc.includes('/if/flow/')) return loc;
  } catch {
    /* not JSON */
  }
  return `/if/flow/${DEFAULT_FLOW_SLUG}/?query=${encodeURIComponent(q.toString())}`;
}

export function decodeJwtClaims(jwt?: string): FlowClaims | null {
  if (!jwt) return null;
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((ch) => '%' + ch.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as FlowClaims;
  } catch {
    return null;
  }
}

function isExecutorRedirect(to?: string): boolean {
  return !!to && to.includes('/api/v3/flows/executor/');
}

function callbackCode(url: string): string | null {
  if (!url || !url.includes('/auth/callback') || !url.includes('code=')) return null;
  try {
    return new URL(url, window.location.origin).searchParams.get('code');
  } catch {
    return null;
  }
}

function isAccessDenied(url: string, body: string): boolean {
  if (!url.includes('/application/o/authorize/')) return false;
  const b = body.toLowerCase();
  return b.includes('permission denied') || b.includes('access denied') || b.includes('ak-stage-access-denied') || b.includes('request has been denied');
}

function deniedError(c: Challenge): AuthError {
  const secs = parseLocked(c.error_message);
  if (secs != null) return new AuthError('locked', lockedMessage(secs), secs);
  return new AuthError('denied', AUTH_COPY.denied);
}

function asAuthError(e: unknown): AuthError {
  if (e instanceof AuthError) return e;
  if (e instanceof TypeError) return new AuthError('network', AUTH_COPY.network);
  return new AuthError('unexpected', AUTH_COPY.unexpected);
}

/** The `field_key` of every field an ak-stage-prompt challenge asks for. */
function fieldKeys(c: Challenge): string[] {
  const fields = Array.isArray(c.fields) ? (c.fields as Array<{ field_key?: unknown }>) : [];
  return fields.map((f) => f?.field_key).filter((k): k is string => typeof k === 'string');
}

/** The first human sentence in `response_errors` (Authentik puts ak_message / validation text there). */
export function firstError(c: { response_errors?: Record<string, Array<{ string?: string }>> }): string | null {
  for (const list of Object.values(c.response_errors ?? {})) {
    for (const e of list ?? []) if (typeof e?.string === 'string' && e.string.trim()) return e.string.trim();
  }
  return null;
}

function hasErrors(c: Challenge): boolean {
  return !!c.response_errors && Object.keys(c.response_errors).length > 0;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}
